import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import api from 'utils/api';
import { getApiErrorMessage } from 'utils/apiErrors';
import { fetchBusiness } from 'modules/Business/actions';

const FUNDING_FALLBACK_MESSAGE =
  'We couldn’t add funds to your balance. No funds were added. Please review the payment details and try again.';
const FUNDING_OUTCOME_UNCONFIRMED_MESSAGE =
  'We couldn’t confirm whether the payment was recorded. To avoid a duplicate payment, don’t try it again yet. Check your balance or payment activity, or contact support.';

const attemptStorageKey = (businessId, amount) =>
  `business-funding-attempt:${businessId}:${amount}`;

const fundingAttemptId = (businessId, amount) => {
  const key = attemptStorageKey(businessId, amount);
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const created = window.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(key, created);
  return created;
};

const useBusinessFunding = (businessId) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const requestInProgress = useRef(false);
  const outcomeUnconfirmed = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [requiresPaymentReview, setRequiresPaymentReview] = useState(false);

  const clearError = useCallback(() => {
    if (!outcomeUnconfirmed.current) setErrorMessage('');
  }, []);

  const addFunds = useCallback(async (amount) => {
    if (requestInProgress.current || outcomeUnconfirmed.current) return false;

    requestInProgress.current = true;
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const storageKey = attemptStorageKey(businessId, amount);
      await api.post(`/business/${businessId}/charge/${amount}`, null, {
        headers: { 'Idempotency-Key': fundingAttemptId(businessId, amount) },
      });
      sessionStorage.removeItem(storageKey);
      enqueueSnackbar('Amount added to your balance', { variant: 'success' });
      await dispatch(fetchBusiness(businessId, true));
      return true;
    } catch (error) {
      const status = error?.response?.status ?? error?.response?.data?.status ?? error?.status;
      const paymentNeedsReview = !Number.isInteger(status) || status === 409 || status >= 500;
      if (!paymentNeedsReview) {
        sessionStorage.removeItem(attemptStorageKey(businessId, amount));
      }
      const message = paymentNeedsReview
        ? FUNDING_OUTCOME_UNCONFIRMED_MESSAGE
        : getApiErrorMessage(error, FUNDING_FALLBACK_MESSAGE);
      outcomeUnconfirmed.current = paymentNeedsReview;
      setRequiresPaymentReview(paymentNeedsReview);
      setErrorMessage(message);
      enqueueSnackbar(message, { variant: 'error' });
      return false;
    } finally {
      requestInProgress.current = false;
      setIsSubmitting(false);
    }
  }, [businessId, dispatch, enqueueSnackbar]);

  return {
    addFunds,
    clearError,
    errorMessage,
    isSubmitting,
    requiresPaymentReview,
  };
};

export default useBusinessFunding;
